/**
 * The hangar's orbit camera as pure state (Phase 6): a slow automatic turn that the pilot can
 * take over by dragging, inertia after a release, pitch and distance limits, and framing from
 * the mech's height. No Three.js here so the behaviour is unit-tested.
 */

export interface OrbitConfig {
  /** Radians per second of the automatic turn. */
  autoSpeed: number;
  /** Seconds after the last drag before the automatic turn resumes. */
  idleSeconds: number;
  /** Radians per pixel of drag. */
  dragSensitivity: number;
  /** Fraction of the release velocity kept per second (0..1). */
  friction: number;
  minPitch: number;
  maxPitch: number;
  minRadius: number;
  maxRadius: number;
}

export const ORBIT_DEFAULTS: OrbitConfig = {
  autoSpeed: 0.18,
  idleSeconds: 4,
  dragSensitivity: 0.006,
  friction: 0.05,
  minPitch: -0.15,
  maxPitch: 0.9,
  minRadius: 4,
  maxRadius: 30,
};

export interface OrbitState {
  yaw: number;
  pitch: number;
  radius: number;
  /** The point the camera looks at (metres). */
  target: { x: number; y: number; z: number };
  /** Yaw velocity from the last drag, radians per second. */
  yawVelocity: number;
  /** Seconds since the pilot last dragged. */
  idleFor: number;
}

export interface OrbitInput {
  /** Pointer movement while dragging this frame, pixels. */
  dragX: number;
  dragY: number;
  dragging: boolean;
  /** Wheel delta this frame (positive = away). */
  wheel: number;
}

export const NO_ORBIT_INPUT: OrbitInput = { dragX: 0, dragY: 0, dragging: false, wheel: 0 };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The framing for a mech of `boundsHeight` metres: the camera looks at its middle. */
export function orbitFraming(boundsHeight: number): Pick<OrbitState, 'radius' | 'target'> {
  const height = Math.max(1, boundsHeight);
  return { radius: height * 2.1, target: { x: 0, y: height * 0.48, z: 0 } };
}

export function initialOrbit(boundsHeight: number): OrbitState {
  return {
    yaw: 0.6,
    pitch: 0.18,
    yawVelocity: 0,
    idleFor: ORBIT_DEFAULTS.idleSeconds,
    ...orbitFraming(boundsHeight),
  };
}

export function orbitStep(
  state: OrbitState,
  input: OrbitInput,
  dt: number,
  cfg: OrbitConfig = ORBIT_DEFAULTS,
): OrbitState {
  const step = Math.max(0, Math.min(dt, 0.25));
  let { yaw, pitch, radius, yawVelocity, idleFor } = state;
  if (input.dragging) {
    const dYaw = -input.dragX * cfg.dragSensitivity;
    yaw += dYaw;
    pitch = clamp(pitch + input.dragY * cfg.dragSensitivity, cfg.minPitch, cfg.maxPitch);
    yawVelocity = step > 0 ? dYaw / step : 0;
    idleFor = 0;
  } else {
    idleFor += step;
    if (Math.abs(yawVelocity) > 1e-4) {
      yaw += yawVelocity * step;
      yawVelocity *= Math.pow(cfg.friction, step);
      if (Math.abs(yawVelocity) < 1e-3) yawVelocity = 0;
    } else if (idleFor >= cfg.idleSeconds) {
      yaw += cfg.autoSpeed * step;
    }
  }
  if (input.wheel !== 0) {
    radius = clamp(radius * Math.exp(input.wheel * 0.0012), cfg.minRadius, cfg.maxRadius);
    idleFor = 0;
  }
  // Keep the yaw bounded so a long session never loses precision.
  if (yaw > Math.PI * 2) yaw -= Math.PI * 2;
  if (yaw < -Math.PI * 2) yaw += Math.PI * 2;
  return { ...state, yaw, pitch, radius, yawVelocity, idleFor };
}

/** Camera position for the state (spherical about the target, pitch above the horizon). */
export function orbitPosition(state: OrbitState): { x: number; y: number; z: number } {
  const horizontal = Math.cos(state.pitch) * state.radius;
  return {
    x: state.target.x + Math.sin(state.yaw) * horizontal,
    y: state.target.y + Math.sin(state.pitch) * state.radius,
    z: state.target.z + Math.cos(state.yaw) * horizontal,
  };
}

/** Re-frames for another mech without losing the pilot's angle. */
export function reframeOrbit(state: OrbitState, boundsHeight: number): OrbitState {
  return { ...state, ...orbitFraming(boundsHeight) };
}
